import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Shield, Eye, EyeOff, AlertCircle, Smartphone, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { adminLogin, adminVerifyMFA, adminChangePassword } from '@/admin/services/adminAuthService';
import { useToast } from '@/components/ui/use-toast';

type LoginStep = 'login' | 'mfa' | 'newPassword' | 'mfaSetup';

const AdminLogin: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState<LoginStep>('login');
  const [session, setSession] = useState('');
  const [mfaSecretCode, setMfaSecretCode] = useState('');
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    mfaCode: '',
    newPassword: '',
    confirmPassword: '',
  });

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await adminLogin(formData.email, formData.password);
      
      if (result.success) {
        toast({
          title: 'Login successful',
          description: 'Welcome to the admin portal',
        });
        navigate('/admin/dashboard');
      } else if (result.requiresNewPassword) {
        setSession(result.session!);
        setStep('newPassword');
      } else if (result.requiresMFA) {
        setSession(result.session!);
        setStep('mfa');
      } else {
        setError(result.error || 'Login failed');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMFAVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await adminVerifyMFA(session, formData.mfaCode, formData.email);
      
      if (result.success) {
        toast({
          title: 'MFA verification successful',
          description: 'Welcome to the admin portal',
        });
        navigate('/admin/dashboard');
      } else {
        setError(result.error || 'MFA verification failed');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.newPassword.length < 12) {
      setError('Password must be at least 12 characters long');
      return;
    }

    setLoading(true);

    try {
      const result = await adminChangePassword(session, formData.email, formData.newPassword);
      
      if (result.success) {
        toast({
          title: 'Password changed successfully',
          description: 'Welcome to the admin portal',
        });
        navigate('/admin/dashboard');
      } else if (result.requiresMFASetup) {
        setSession(result.session!);
        setMfaSecretCode(result.secretCode!);
        setStep('mfaSetup');
        toast({
          title: 'MFA Setup Required',
          description: 'Please set up multi-factor authentication to continue',
        });
      } else if (result.requiresMFA) {
        setSession(result.session!);
        setStep('mfa');
      } else {
        setError(result.error || 'Failed to change password');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const renderLoginForm = () => (
    <form onSubmit={handleLogin}>
      <CardHeader>
        <CardTitle>Sign in to your account</CardTitle>
        <CardDescription>
          Enter your admin credentials to continue
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <Label htmlFor="email">Email address</Label>
          <Input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={formData.email}
            onChange={handleInputChange}
            placeholder="admin@cecurestream.com"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              required
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? (
                <EyeOff className="h-4 w-4 text-gray-500" />
              ) : (
                <Eye className="h-4 w-4 text-gray-500" />
              )}
            </Button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="text-sm">
            <Link
              to="/admin/forgot-password"
              className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Forgot your password?
            </Link>
          </div>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col space-y-4">
        <Button
          type="submit"
          className="w-full"
          disabled={loading}
        >
          {loading ? 'Signing in...' : 'Sign in'}
        </Button>
        
        <div className="text-center text-sm text-gray-600 dark:text-gray-400">
          <p>Not an admin?</p>
          <Link
            to="/signin"
            className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Go to main app →
          </Link>
        </div>
      </CardFooter>
    </form>
  );

  const renderMFAForm = () => (
    <form onSubmit={handleMFAVerification}>
      <CardHeader>
        <CardTitle>Two-Factor Authentication</CardTitle>
        <CardDescription>
          Enter the 6-digit code from your authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-blue-100 dark:bg-blue-900 p-4">
            <Smartphone className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="mfaCode">Verification Code</Label>
          <Input
            id="mfaCode"
            name="mfaCode"
            type="text"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={formData.mfaCode}
            onChange={handleInputChange}
            placeholder="000000"
            className="text-center text-2xl font-mono"
          />
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
          Open your authenticator app and enter the 6-digit code
        </p>
      </CardContent>
      <CardFooter className="flex flex-col space-y-4">
        <Button
          type="submit"
          className="w-full"
          disabled={loading}
        >
          {loading ? 'Verifying...' : 'Verify'}
        </Button>
        
        <Button
          type="button"
          variant="outline"
          className="w-full"
          onClick={() => {
            setStep('login');
            setError('');
            setFormData({ ...formData, mfaCode: '' });
          }}
        >
          Back to login
        </Button>
      </CardFooter>
    </form>
  );

  const renderPasswordChangeForm = () => (
    <form onSubmit={handlePasswordChange}>
      <CardHeader>
        <CardTitle>Change Your Password</CardTitle>
        <CardDescription>
          Your temporary password has expired. Please set a new password.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-center mb-4">
          <div className="rounded-full bg-blue-100 dark:bg-blue-900 p-4">
            <Lock className="h-8 w-8 text-blue-600 dark:text-blue-400" />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="newPassword">New Password</Label>
          <div className="relative">
            <Input
              id="newPassword"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              required
              value={formData.newPassword}
              onChange={handleInputChange}
              placeholder="Enter new password"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
              onClick={() => setShowNewPassword(!showNewPassword)}
            >
              {showNewPassword ? (
                <EyeOff className="h-4 w-4 text-gray-500" />
              ) : (
                <Eye className="h-4 w-4 text-gray-500" />
              )}
            </Button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <Input
            id="confirmPassword"
            name="confirmPassword"
            type="password"
            required
            value={formData.confirmPassword}
            onChange={handleInputChange}
            placeholder="Confirm new password"
          />
        </div>

        <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
          <p>Password must:</p>
          <ul className="list-disc list-inside ml-2">
            <li>Be at least 12 characters long</li>
            <li>Include uppercase and lowercase letters</li>
            <li>Include at least one number</li>
            <li>Include at least one special character</li>
          </ul>
        </div>
      </CardContent>
      <CardFooter>
        <Button
          type="submit"
          className="w-full"
          disabled={loading}
        >
          {loading ? 'Changing password...' : 'Change Password'}
        </Button>
      </CardFooter>
    </form>
  );

  const renderMFASetupForm = () => (
    <Card>
      <CardHeader>
        <CardTitle>Set Up Two-Factor Authentication</CardTitle>
        <CardDescription>
          Scan the QR code with your authenticator app
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            MFA setup is required for all admin accounts. Please use Google Authenticator, Authy, or a similar app.
          </AlertDescription>
        </Alert>

        <div className="text-center space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Secret code: <code className="font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">{mfaSecretCode}</code>
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            After adding the account to your authenticator app, enter the 6-digit code to continue.
          </p>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <div className="rounded-full bg-blue-100 dark:bg-blue-900 p-4">
              <Shield className="h-12 w-12 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <h2 className="mt-6 text-3xl font-bold text-gray-900 dark:text-white">
            Admin Portal
          </h2>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Sign in to access the CecureStream admin dashboard
          </p>
        </div>

        <Card>
          {step === 'login' && renderLoginForm()}
          {step === 'mfa' && renderMFAForm()}
          {step === 'newPassword' && renderPasswordChangeForm()}
          {step === 'mfaSetup' && renderMFASetupForm()}
        </Card>

        <div className="text-center">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            This is a secure area. All login attempts are monitored and logged.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;